import React from 'react';
import { has, compose, map, cond, is, identity, join, isNil, always, T, omit } from 'ramda';
import { Grid, Row, Col } from 'react-flexbox-grid';

import { formatDate, formatDgDate, validate, formatHeaderDate } from 'lib/dateDisplay';

import { getLatestFinishedCommitData } from 'lib/documentHandling';
import { getLatestRankedDiagnosis } from 'lib/diagnosis';

import HistoryEntryHeader from 'components/HistoryEntryHeader';
import { sectionTitle } from 'components/FormSection/FormSection.less';
import CommonHistoryEntryControls from 'routes/Documents/components/CommonHistoryEntryControls';

import { optTo, editLabel } from 'components/EditLog/EditLog.less';
import { computeBMI, labelAccessor, optionAccessor, sectionAccessor, valueExists } from './utils';
import { dateFromPartial, isPartialDate } from 'neuro-utils';

const handleOpt = (msg, opt) =>
  cond([
    [is(Number), identity],
    [isPartialDate, formatDgDate],
    [is(Array), compose(join(', '), map(msg))],
    [isNil, always('')],
    [T, msg],
  ])(opt);

const makeOptMessageHandler = (accessor, formatMessage, messages) => msg =>
  cond([
    [has(accessor(msg)), x => formatMessage({ id: accessor(msg) })],
    [always(validate(msg)), always(formatDate(msg))],
    [has(`dictionary.${msg}`), x => formatMessage({ id: `dictionary.${msg}` })],
    [has(msg), x => formatMessage({ id: msg })],
    [T, always(msg)],
  ])(messages);

const renderEditsPerDay = (section, label, opt) => edits => {
  const titles = [...sections];

  const getTitle = field => {
    const matchingTitle = titles.find(t => fieldsBySection[t].includes(field));
    if (matchingTitle) {
      titles.splice(titles.indexOf(matchingTitle), 1);
      return matchingTitle;
    }
    return undefined;
  };

  const getValue = (field, value) => {
    switch (field) {
      case 'workAbilityComparedToLifetimeBest':
      case 'selfAssessmentAbilityToWorkIn2Years':
      case 'reductionType':
        return `${field}.${value}`;
      default:
        return value;
    }
  };

  return (
    <Grid fluid>
      {map(e => {
        const title = getTitle(e.field);
        return (
          <React.Fragment key={`${e.field}_${e.value}`}>
            {title && (
              <Row className={editLabel}>
                <Col xs={12} sm={4}>
                  <h6 className={sectionTitle} style={{ padding: 0 }}>
                    {section ? section(title) : title}
                  </h6>
                </Col>
              </Row>
            )}
            <Row className={editLabel}>
              <Col xs={12} sm={4}>
                <div>{label(e.field)}</div>
              </Col>
              <Col xs={4} sm={4}>
                <div className={optTo}>{handleOpt(opt, getValue(e.field, e.value))}</div>
              </Col>
            </Row>
          </React.Fragment>
        );
      })(edits)}
    </Grid>
  );
};

export const sorter = docs =>
  docs.sort(
    (d1, d2) =>
      (isPartialDate(d2?.content?.date) ? dateFromPartial(d2.content.date) : d2.createDate) -
      (isPartialDate(d1?.content?.date) ? dateFromPartial(d1.content.date) : d1.createDate),
  );

const keyToFieldMap = new Map([
  ['diagnosis', 'diagnosisClass'],
  ['date', 'diagnosisDate'],
  ['type', 'diagnosisType'],
]);

/**
 * @note The following fields are legacy:
 * - bpSyst
 * - bpDiast
 * - incomeClass
 */
const fieldsBySection = {
  familyPrevalence: ['familyPrevalence', 'familyPrevalenceDetail'],
  weightAndHeight: ['patientLength', 'patientWeight'],
  bloodPressure: ['bpSyst', 'bpDiast'],
  childrenAndPregnancies: [
    'numberOfChildren',
    'reproductiveStatus',
    'pregnancyDueDate',
    'usingContraceptive',
    'contraceptives',
  ],
  familyRelations: ['maritialStatus'],
  living: ['accomodation'],
  education: ['baseEducation', 'furtherEducation', 'educationYears'],
  worklife: [
    'incomeClass',
    'currentStatus',
    'partTimeWorkedHours',
    'sickLeaveDaysPassed',
    'unemployedDaysPassed',
    'retirementType',
    'partTimePensionWorkedHours',
    'occupationBeforePension',
    'occupation',
    'statusAsEmployee',
    'otherEmployeeDetail',
    //
    'workAbilityComparedToLifetimeBest',
    'reductionReason',
    'reductionType',
    //
    'selfAssessmentAbilityToWorkIn2Years',
  ],
};

const sections = Object.keys(fieldsBySection);

const fields = [
  ...fieldsBySection.familyPrevalence,
  ...fieldsBySection.weightAndHeight,
  ...fieldsBySection.bloodPressure,
  ...fieldsBySection.childrenAndPregnancies,
  ...fieldsBySection.familyRelations,
  ...fieldsBySection.living,
  ...fieldsBySection.education,
  ...fieldsBySection.worklife,
];

export const diagnosisMapper =
  () =>
  (diagnoses, { formatMessage, messages }) => {
    const latestDg = getLatestRankedDiagnosis(diagnoses);
    if (!latestDg) return null;

    const keys = ['diagnosis', 'date', 'type'].filter(key => latestDg[key]);

    const fieldValuePairs = keys.map(key => ({
      field: keyToFieldMap.get(key),
      value: key === 'type' && latestDg[key] ? `course.${latestDg[key]}` : latestDg[key],
    }));

    const labelMessage = x => formatMessage({ id: labelAccessor(x || '') });
    const optionMessage = makeOptMessageHandler(id => `diagnosis.${id}`, formatMessage, messages);

    return (
      <React.Fragment>{renderEditsPerDay(undefined, labelMessage, optionMessage)(fieldValuePairs)}</React.Fragment>
    );
  };

const historyMapper =
  (name, edit) =>
  (d, { formatMessage, messages }) => {
    let { content } = d;
    // Flatten/un-nest workAbilityScore values
    if (
      content &&
      typeof content === 'object' &&
      content.workAbilityScore &&
      typeof content.workAbilityScore === 'object'
    ) {
      const { workAbilityComparedToLifetimeBest, reductionReason, reductionType } = content.workAbilityScore;
      content = {
        ...omit(['workAbilityScore'], content),
        workAbilityComparedToLifetimeBest,
        reductionReason,
        reductionType,
      };
    }
    const keys = fields.filter(key => valueExists(content[key]));

    if (keys.includes('patientLength') && keys.includes('patientWeight')) {
      keys.splice(keys.indexOf('patientWeight') + 1, 0, 'bmi');
    }

    const fieldValuePairs = keys.map(key => {
      if (key === 'bmi') {
        const bmi = computeBMI(content);
        return { field: key, value: !isNaN(bmi) ? bmi : '-' };
      }
      return { field: key, value: content[key] };
    });

    const sectionMessage = x => formatMessage({ id: sectionAccessor(x || '') });
    const labelMessage = x => formatMessage({ id: labelAccessor(x || '') });
    const optionMessage = makeOptMessageHandler(optionAccessor, formatMessage, messages);

    const dateStamp = d?.content?.date ? dateFromPartial(d.content.date).getTime() : d.createDate;

    return (
      <div key={d.documentId}>
        <HistoryEntryHeader title={formatHeaderDate(dateStamp)}>
          <CommonHistoryEntryControls action={() => edit(name, d.documentId, getLatestFinishedCommitData(d))} />
        </HistoryEntryHeader>
        {renderEditsPerDay(sectionMessage, labelMessage, optionMessage)(fieldValuePairs)}
      </div>
    );
  };

export default historyMapper;
